import React, { useRef, useState, useCallback, useEffect } from "react";
import ReactDOM from "react-dom";
import { X, ChevronLeft, ChevronRight, Play, Pause, Scissors, Loader2 } from "lucide-react";
import { useTheme } from "../../../context/ThemeContext";
import { useToast } from "../../../context/ToastContext";
import { uploadBase64ToCos } from "../../../utils/fileUtils";

const FRAME_STEP = 1 / 25; // ~0.04s per frame (25fps)

const FrameCaptureModal = ({ videoUrl, onConfirm, onClose, exiting }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const progressRef = useRef(null);
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [previewDataUrl, setPreviewDataUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // 截取当前帧到 canvas
  const captureFrame = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) return null;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/png");
  }, []);

  // 视频暂停时自动更新预览
  const updatePreview = useCallback(() => {
    const dataUrl = captureFrame();
    if (dataUrl) setPreviewDataUrl(dataUrl);
  }, [captureFrame]);

  // 视频事件
  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration);
    video.currentTime = 0;
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    setCurrentTime(video.currentTime);
    if (video.paused) updatePreview();
  }, [updatePreview]);

  const handleSeeked = useCallback(() => {
    updatePreview();
  }, [updatePreview]);

  // 播放/暂停
  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPlaying(true);
    } else {
      video.pause();
      setIsPlaying(false);
      updatePreview();
    }
  }, [updatePreview]);

  // 视频播放结束
  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    updatePreview();
  }, [updatePreview]);

  // 逐帧前进/后退
  const stepFrame = useCallback((direction) => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setIsPlaying(false);
    const newTime = Math.max(0, Math.min(video.duration, video.currentTime + direction * FRAME_STEP));
    video.currentTime = newTime;
  }, []);

  // 进度条拖拽
  const seekToPosition = useCallback((clientX) => {
    const video = videoRef.current;
    const bar = progressRef.current;
    if (!video || !bar) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
  }, []);

  const handleProgressMouseDown = useCallback((e) => {
    setIsDragging(true);
    const video = videoRef.current;
    if (video && !video.paused) {
      video.pause();
      setIsPlaying(false);
    }
    seekToPosition(e.clientX);
  }, [seekToPosition]);

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e) => seekToPosition(e.clientX);
    const handleMouseUp = () => setIsDragging(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, seekToPosition]);

  // 确认截取
  const handleConfirm = useCallback(async () => {
    const dataUrl = captureFrame();
    if (!dataUrl) {
      toast.error("截取失败，请重试");
      return;
    }
    setIsUploading(true);
    try {
      const cosUrl = await uploadBase64ToCos(dataUrl);
      onConfirm(cosUrl);
    } catch (e) {
      toast.error("上传失败: " + e.message);
    } finally {
      setIsUploading(false);
    }
  }, [captureFrame, onConfirm, toast]);

  // 格式化时间
  const formatTime = (t) => {
    if (!t || isNaN(t)) return "0:00.00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    const ms = Math.floor((t % 1) * 100);
    return `${m}:${s.toString().padStart(2, "0")}.${ms.toString().padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return ReactDOM.createPortal(
    <div
      className={`fixed inset-0 z-[500] flex items-center justify-center ${exiting ? "animate-out fade-out duration-150" : "animate-in fade-in duration-200"}`}
      style={{ backgroundColor: "var(--overlay-heavy)" }}
      onClick={onClose}
    >
      <div
        className={`relative rounded-2xl shadow-2xl overflow-hidden flex flex-col ${exiting ? "animate-out fade-out zoom-out-95 duration-150" : "animate-in fade-in zoom-in-95 duration-200"}`}
        style={{
          backgroundColor: "var(--color-bg-primary)",
          border: "1px solid var(--color-border-secondary)",
          width: "min(720px, 90vw)",
          maxHeight: "85vh",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 标题栏 */}
        <div
          className="flex items-center justify-between px-5 py-3"
          style={{ borderBottom: "1px solid var(--color-border-secondary)" }}
        >
          <span className="text-sm font-semibold" style={{ color: "var(--color-text-primary)" }}>
            截取视频帧
          </span>
          <button
            onClick={onClose}
            className="p-1 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
            style={{ color: "var(--color-text-muted)" }}
          >
            <X size={16} />
          </button>
        </div>

        {/* 视频区域 */}
        <div className="px-5 pt-4">
          {/* 视频信箱黑边：媒体内容底色，纯黑与主题无关，保留 */}
          <div className="relative rounded-xl overflow-hidden" style={{ backgroundColor: "#000" }}>
            <video
              ref={videoRef}
              src={videoUrl}
              className="w-full"
              style={{ maxHeight: "400px", objectFit: "contain" }}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onSeeked={handleSeeked}
              onEnded={handleEnded}
              muted
              playsInline
              crossOrigin="anonymous"
            />
          </div>
        </div>

        {/* 控制区 */}
        <div className="px-5 py-3 flex flex-col gap-3">
          {/* 进度条 */}
          <div
            ref={progressRef}
            className="relative h-3 rounded-full cursor-pointer group"
            style={{ backgroundColor: "var(--color-bg-tertiary)" }}
            onMouseDown={handleProgressMouseDown}
          >
            {/* 已播放部分 */}
            <div
              className="absolute top-0 left-0 h-full rounded-full transition-[width] duration-75"
              style={{
                width: `${progress}%`,
                backgroundColor: theme.colors.accentPrimary,
              }}
            />
            {/* 拖拽手柄 */}
            <div
              className="absolute top-1/2 -translate-y-1/2 w-4 h-4 rounded-full shadow-md transition-transform group-hover:scale-125"
              style={{
                left: `calc(${progress}% - 8px)`,
                backgroundColor: "var(--color-accent-contrast)",
                border: `2px solid ${theme.colors.accentPrimary}`,
              }}
            />
          </div>

          {/* 按钮行 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {/* 播放/暂停 */}
              <button
                onClick={togglePlay}
                className="p-2 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
                style={{ color: "var(--color-text-primary)" }}
                title={isPlaying ? "暂停" : "播放"}
              >
                {isPlaying ? <Pause size={16} /> : <Play size={16} />}
              </button>
              {/* 后退一帧 */}
              <button
                onClick={() => stepFrame(-1)}
                className="p-2 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
                style={{ color: "var(--color-text-primary)" }}
                title="后退一帧"
              >
                <ChevronLeft size={16} />
              </button>
              {/* 前进一帧 */}
              <button
                onClick={() => stepFrame(1)}
                className="p-2 rounded-lg transition-colors hover:bg-[var(--color-bg-tertiary)]"
                style={{ color: "var(--color-text-primary)" }}
                title="前进一帧"
              >
                <ChevronRight size={16} />
              </button>
              {/* 时间显示 */}
              <span className="text-xs font-mono ml-1" style={{ color: "var(--color-text-muted)" }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            {/* 截取按钮 */}
            <button
              onClick={handleConfirm}
              disabled={isUploading || !previewDataUrl}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                backgroundColor: isUploading ? `${theme.colors.accentPrimary}60` : theme.colors.accentPrimary,
                color: "var(--color-accent-contrast)",
                opacity: !previewDataUrl ? 0.5 : 1,
              }}
            >
              {isUploading ? (
                <><Loader2 size={14} className="animate-spin" /> 上传中...</>
              ) : (
                <><Scissors size={14} /> 截取此帧并插入新分镜</>
              )}
            </button>
          </div>
        </div>

        {/* 隐藏 canvas */}
        <canvas ref={canvasRef} className="hidden" />
      </div>
    </div>,
    document.body
  );
};

export default React.memo(FrameCaptureModal);
